<template>
  <div v-if="!loading">
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Edit Team: {{ team.name }}</h4>
          </div>
          <div class="card-body">
            <ValidationAlert :errors="errors" />
            <BForm @submit.prevent="updateTeam">
              <BFormGroup label="Team Size" label-for="team-size-id">
                <BFormSelect
                  id="team-size-id"
                  v-model="team.team_size_id"
                  :options="teamSizeOptions"
                ></BFormSelect>
              </BFormGroup>
              <BFormGroup label="Agency" label-for="is-agency">
                <BFormCheckbox
                  id="is-agency"
                  v-model="team.is_agency"
                ></BFormCheckbox>
              </BFormGroup>
              <BFormGroup
                label="Auto-Bump Interval (days)"
                label-for="auto-bump-interval-days"
                description="Leave empty to use the global default (10 days). Must be between 1 and 30."
              >
                <BFormInput
                  id="auto-bump-interval-days"
                  v-model.number="team.auto_bump_interval_days"
                  type="number"
                  min="1"
                  max="30"
                  placeholder="10 (default)"
                ></BFormInput>
              </BFormGroup>
              <BFormGroup
                label="Custom Featured Slot Limit"
                label-for="featured-slots-custom-limit"
                description="Leave empty to use the purchased tier. Set it for custom deals (more than 20 slots) billed manually in Stripe."
              >
                <BFormInput
                  id="featured-slots-custom-limit"
                  v-model.number="team.featured_slots_custom_limit"
                  type="number"
                  min="1"
                  max="500"
                  placeholder="Purchased tier"
                  aria-describedby="featured-slots-usage"
                ></BFormInput>
                <small id="featured-slots-usage" class="form-text text-muted">
                  Purchased tier: {{ featuredSlots.tier || "None" }} · In use:
                  {{ featuredSlots.used }}
                </small>
              </BFormGroup>
              <BButton
                :disabled="isPosting"
                variant="secondary"
                @click="goBack"
              >
                Back
              </BButton>
              <BButton :disabled="isPosting" variant="success" type="submit">
                Save
              </BButton>
            </BForm>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import {
  BForm,
  BFormGroup,
  BFormSelect,
  BFormCheckbox,
  BFormInput,
  BButton
} from "bootstrap-vue";
import ValidationAlert from "@/components/Forms/ValidationAlert.vue";

export default {
  components: {
    ValidationAlert,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormCheckbox,
    BFormInput,
    BButton
  },
  props: {
    teamId: {
      required: true,
      type: [String, Number]
    }
  },
  data() {
    return {
      team: {},
      featuredSlots: { tier: null, used: 0 },
      loadedCustomLimit: null,
      teamSizes: [],
      isPosting: false,
      errors: [],
      loading: true
    };
  },
  computed: {
    teamSizeOptions() {
      return this.teamSizes.map(size => ({
        value: size.id,
        text: (size.label && (size.label.fr || size.label.en)) || `Size ${size.id}`
      }));
    }
  },
  created() {
    Promise.all([
      this.$http.get(`/enroll_admin/teams/${this.teamId}`),
      this.$http.get(`/enroll_admin/team-sizes`)
    ])
      .then(([teamResponse, sizesResponse]) => {
        this.setTeam(teamResponse.data);
        this.teamSizes = sizesResponse.data;
      })
      .catch(() => {
        Vue.$toast.error("Failed to load team");
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    setTeam(data) {
      // The custom limit is read from `featured_slots` and edited as a flat field.
      const slots = data.featured_slots || {};
      this.featuredSlots = { tier: slots.tier || null, used: slots.used || 0 };
      this.loadedCustomLimit =
        slots.custom_limit === undefined ? null : slots.custom_limit;
      this.team = {
        ...data,
        featured_slots_custom_limit:
          slots.custom_limit === undefined ? null : slots.custom_limit
      };
    },
    refreshTeam() {
      return this.$http
        .get(`/enroll_admin/teams/${this.teamId}`)
        .then(({ data }) => this.setTeam(data));
    },
    updateTeam() {
      this.errors = [];
      this.isPosting = true;
      const payload = {
        team_size_id: this.team.team_size_id,
        is_agency: this.team.is_agency,
        // v-model.number leaves "" as a string when the field is cleared; API treats null as "use global default".
        auto_bump_interval_days:
          this.team.auto_bump_interval_days === "" ||
          this.team.auto_bump_interval_days === undefined
            ? null
            : this.team.auto_bump_interval_days
      };
      // Sent only when changed: the API locks the team's featured slots for this field (Codex L2F-3).
      const customLimit =
        this.team.featured_slots_custom_limit === "" ||
        this.team.featured_slots_custom_limit === undefined
          ? null
          : this.team.featured_slots_custom_limit;
      if (customLimit !== this.loadedCustomLimit) {
        payload.featured_slots_custom_limit = customLimit;
      }
      this.$http
        .put(`/enroll_admin/teams/${this.teamId}`, payload)
        .then(({ data }) => {
          // Lowering the limit below usage releases featured offers (D28).
          const released = data && data.released_count;
          Vue.$toast.success(
            released > 0
              ? `Team updated — ${released} featured offer${
                  released === 1 ? "" : "s"
                } removed`
              : "Team updated"
          );
          return this.refreshTeam();
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.errors = error.response.data;
          }
          // 409 featured_slots_busy: the global handler (main.js) already toasts the API message.
        })
        .finally(() => {
          this.isPosting = false;
        });
    },
    goBack() {
      this.$router.push({
        name: "team",
        params: { teamId: this.teamId }
      });
    }
  }
};
</script>
